import React from 'react';
import { RemiseExtraField } from '../PrestationPlanning/RemiseExtraField';
import { etendreOptionsParCible } from '../Prestations/prestationsTypes';
import { Loader2, CalendarClock, CheckCircle2, Circle, Clock, Trash2, AlertCircle } from 'lucide-react';
import { moduleApi } from '../../lib/api';
import { Button } from '../ui/Button';
import { formatDureeMinutes } from '../PrestationPlanning/prestationPlanningShared';

interface DevisItem {
    catalog_item_id: string | null;
    label: string;
    quantity: number;
    unit_price_ttc?: number;
    tax_rate?: number;
    line_ttc?: number;
    is_discount_line?: boolean;
    passage_number?: number | null;
    passage_label?: string | null;
}
interface FormulaAppointment {
    id: string;
    appointment_date: string;
    start_time: string;
    status: string;
    formula_occurrence_number: number | null;
    total_amount_ttc: number;
    completed_at: string | null;
    catalog_items: { catalog_item_id: string | null; label: string; quantity: number }[];
}
interface CatalogItem { id: string; label: string; unit_price_ttc: number; tax_rate: number; unit_type?: string; default_duration_minutes?: number; category_id?: string | null; }
interface Employe { id: string; first_name: string; last_name: string; }
interface FormulaItemRule {
    catalog_item_id: string; label: string;
    discount_type: 'none' | 'percent' | 'amount'; discount_value: number;
    free_occurrence_numbers: number[];
    free_max_quantity?: number;
}
interface CategoryThreshold { category_id: string; category_name: string; minimum_quantity: number; }
interface FormulaInfo {
    formula_name: string; occurrences_total: number; occurrences_used: number; passages_libres?: boolean;
    formula_items?: FormulaItemRule[];
    category_thresholds?: CategoryThreshold[];
    category_thresholds_mode?: 'any' | 'all';
    threshold_kind?: 'quantite' | 'montant';
    minimum_amount_ttc?: number;
}
// Une ligne du rendez-vous en cours de préparation : reprise du devis, mais ajoutable/retirable/ajustable.
interface LigneRdv {
    catalog_item_id: string | null; label: string; quantity: number;
    unit_price_ttc: number; tax_rate: number; unit_duration_minutes: number;
    category_id: string | null; unit_type: string;
    /** Remise en %/€ ou unités offertes sur cette ligne (elles figureront sur la facture). */
    discount_type?: 'percent' | 'amount' | 'offered' | null; discount_value?: number; offered_quantity?: number;
}

// L'unité facturée dépend du type de tarif configuré sur la prestation : une moquette réglée « Au m² » se
// compte en m², pas à la pièce (2026-09-06, Emeni : « tapis moquette a été configuré au m², et pourtant
// il me marque 30 mn/u alors que c'est 30 mn/m² »), une banquette « Au mètre linéaire » en ml.
const UNITE_COURTE: Record<string, string> = { m2: 'm²', ml: 'ml', unite: 'u', surface: 'u' };
// Les frais kilométriques sont une ligne du devis, pas un travail : ils ne sont jamais proposés parmi les
// prestations d'un rendez-vous, et n'ont ni durée à saisir ni durée à compter.
const estFraisKm = (l: { label: string }) => /^frais kilom/i.test(String(l.label || '').trim());

const uniteCourte = (unitType?: string) => UNITE_COURTE[unitType || 'unite'] || 'u';

// Même règle que le serveur (computeFormulaItemBenefit) : une ligne offerte sur CE passage l'emporte sur la
// réduction, sinon c'est la réduction propre à cette ligne de la formule qui s'applique.
const beneficeFormule = (regle: FormulaItemRule | undefined, numeroPassage: number) => {
    if (!regle) return null;
    if ((regle.free_occurrence_numbers || []).includes(numeroPassage)) return { offert: true, texte: regle.free_max_quantity && regle.free_max_quantity > 0 ? `Offert jusqu'à ${regle.free_max_quantity}` : 'Offert' };
    if (regle.discount_type === 'percent' && regle.discount_value > 0) return { offert: false, texte: `-${regle.discount_value} %` };
    if (regle.discount_type === 'amount' && regle.discount_value > 0) return { offert: false, texte: `-${regle.discount_value} €` };
    return null;
};

const fmtDate = (d?: string | null) => {
    if (!d) return '—';
    try { return new Date(d.length <= 10 ? `${d}T00:00:00` : d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return d; }
};

/**
 * Suivi des passages d'une formule, DANS le dossier (2026-09-06, demande d'Emeni : « on doit vraiment avoir
 * un historique des passages, ce qui a été fait ou non ») — et surtout planification directe depuis ici.
 *
 * Avant, l'étape rendez-vous disait « créez le rendez-vous dans le Planning puis rattachez-le ici, rien
 * n'est recopié » : il fallait quitter le dossier et ressaisir à la main des prestations pourtant déjà
 * convenues passage par passage avec le client (elles vivent dans les lignes du devis signé, via
 * passage_number). Le bouton « Planifier » reprend exactement ces lignes ; on ne saisit plus que la date,
 * l'heure et le ou les salariés.
 *
 * 2026-09-06 (Emeni) : les lignes reprises restent MODIFIABLES avant de poser le rendez-vous — « la
 * possibilité d'ajouter ou retirer des prestations pour le rendez-vous et il doit toujours appliquer les
 * réductions concernées pour ce client et les offerts ». Les réductions/gratuités de la formule sont
 * affichées ici ligne par ligne pour ce numéro de passage, et réellement appliquées à la clôture par le
 * serveur (POST .../complete), qui reconnaît une prestation de la formule par son catalog_item_id — d'où
 * l'importance de conserver cet identifiant sur une ligne ajoutée à la main.
 */
export function PassagesTracker({
    devisItems, formulaAppointments, formulaInfo, crmContactId, formulaSubscriptionId, dossierId, onBooked,
}: {
    devisItems: DevisItem[];
    formulaAppointments: FormulaAppointment[];
    formulaInfo: FormulaInfo | null;
    crmContactId: string | null;
    formulaSubscriptionId: string | null;
    dossierId?: string;
    /** `avertissement` : le rendez-vous est créé mais quelque chose n'a pas abouti (e-mail de confirmation, lien de paiement). */
    onBooked: (appointmentId: string, avertissement?: string) => void;
}) {
    const [planifiePassage, setPlanifiePassage] = React.useState<number | null>(null);
    // Avancement réel de chaque passage (visites multiples) : fait / planifié / reste, calculé par le serveur.
    interface AvancementPassage { ancien: boolean; avancement: { etat: string; complet: boolean; total_prevu: number; total_fait: number; pourcentage: number; nb_visites: number; lignes: { catalog_item_id: string | null; label: string; prevu: number; fait: number; planifie: number; reste: number }[] } | null }
    const [avancements, setAvancements] = React.useState<Record<number, AvancementPassage>>({});
    React.useEffect(() => {
        if (!dossierId) return;
        let annule = false;
        moduleApi.getPrestationPassagesAvancement(dossierId).then((r: any) => {
            if (annule || !r || r.error) return;
            const parNumero: Record<number, AvancementPassage> = {};
            (r.passages || []).forEach((p: any) => { parNumero[Number(p.numero)] = { ancien: !!p.ancien, avancement: p.avancement }; });
            setAvancements(parNumero);
        }).catch(() => {});
        return () => { annule = true; };
    }, [dossierId, formulaAppointments.length, formulaAppointments.map((a) => a.status).join(',')]);
    const [catalog, setCatalog] = React.useState<CatalogItem[]>([]);
    const [employes, setEmployes] = React.useState<Employe[]>([]);
    const [date, setDate] = React.useState('');
    const [heure, setHeure] = React.useState('09:00');
    const [employeIds, setEmployeIds] = React.useState<string[]>([]);
    const [lieu, setLieu] = React.useState<'domicile' | 'entreprise'>('domicile');
    // Adresses en plus de celle de la fiche client, pour l'e-mail de confirmation (séparées par des virgules).
    const [emailsSupp, setEmailsSupp] = React.useState('');
    const [lignes, setLignes] = React.useState<LigneRdv[]>([]);
    const [ajoutId, setAjoutId] = React.useState('');
    const [saving, setSaving] = React.useState(false);
    const [erreur, setErreur] = React.useState('');

    React.useEffect(() => {
        moduleApi.getBillingSettings().then((r: any) => {
            try { setCatalog(etendreOptionsParCible(JSON.parse(r?.prestations_catalog_devis || r?.prestations_catalog || '[]'))); } catch { setCatalog([]); }
        }).catch(() => setCatalog([]));
        moduleApi.getEmployes().then((r: any) => setEmployes(Array.isArray(r) ? r : [])).catch(() => setEmployes([]));
    }, []);

    // Ce qui était PRÉVU, passage par passage, d'après les lignes du devis (les lignes de remise négatives
    // sont exclues : elles ne décrivent pas une prestation à réaliser sur place).
    // Sans formule (particulier, pro sans engagement — 2026-09-21, Emeni : « ça devrait être le cas à chaque
    // fois »), le devis n'a pas de passages numérotés : toutes ses lignes forment UN rendez-vous, numéro 0,
    // planifié exactement de la même façon. Avant, ces dossiers n'avaient que « rattachez un rendez-vous
    // déjà créé dans le Planning », c'est-à-dire tout ressaisir ailleurs.
    const sansFormule = !formulaSubscriptionId;
    const passages = React.useMemo(() => {
        if (sansFormule) {
            const items = devisItems.filter((l) => !l.is_discount_line);
            return items.length > 0 ? [{ numero: 0, periode: '', items }] : [];
        }
        const parPassage: Record<number, { periode: string; items: DevisItem[] }> = {};
        for (const l of devisItems) {
            if (l.is_discount_line || l.passage_number == null) continue;
            if (!parPassage[l.passage_number]) parPassage[l.passage_number] = { periode: l.passage_label || '', items: [] };
            parPassage[l.passage_number].items.push(l);
        }
        return Object.entries(parPassage)
            .map(([num, v]) => ({ numero: Number(num), ...v }))
            .sort((a, b) => a.numero - b.numero);
    }, [devisItems, sansFormule]);

    // Un passage peut porter PLUSIEURS rendez-vous (2026-09-06, Emeni) : si le client constate que tout n'a
    // pas été fait, le rendez-vous de rattrapage reste rattaché au même passage.
    const rdvsDuPassage = (numero: number) => formulaAppointments.filter((a) => a.formula_occurrence_number === numero);

    const ouvrirPlanification = (numero: number) => {
        const passage = passages.find((p) => p.numero === numero);
        setPlanifiePassage(numero);
        // Visites multiples : la visite reprend ce qu'il RESTE à planifier (modifiable), pas tout le passage une seconde fois.
        const suivi = avancements[numero];
        const reste = suivi && !suivi.ancien && suivi.avancement ? suivi.avancement.lignes.filter((l) => l.reste > 0) : [];
        const items = passage?.items || [];
        const aReprendre = reste.length > 0
            ? items.map((it) => {
                const r = reste.find((x) => (x.catalog_item_id && it.catalog_item_id ? String(x.catalog_item_id) === String(it.catalog_item_id) : x.label.trim().toLowerCase() === it.label.trim().toLowerCase()));
                return r ? { ...it, quantity: r.reste } : null;
            }).filter(Boolean) as DevisItem[]
            : items;
        setLignes(construireLignes(aReprendre));
        setErreur('');
        setDate(''); setHeure('09:00'); setEmployeIds([]); setLieu('domicile'); setEmailsSupp(''); setAjoutId('');
    };

    const changerQuantite = (idx: number, q: number) =>
        setLignes((prev) => prev.map((l, i) => (i === idx ? { ...l, quantity: Math.max(0, Math.round(q) || 0) } : l)));
    const retirerLigne = (idx: number) => setLignes((prev) => prev.filter((_, i) => i !== idx));
    // Une ligne libre (hors catalogue) n'a pas de durée configurée : elle se saisit ICI, au moment de
    // planifier, ligne par ligne (2026-09-22, Emeni). Une ligne du catalogue garde sa durée de référence.
    const changerDuree = (idx: number, m: number) =>
        setLignes((prev) => prev.map((l, i) => (i === idx ? { ...l, unit_duration_minutes: Math.max(0, Math.round(m) || 0) } : l)));
    const ajouterLigne = (catalogItemId: string) => {
        const ref = catalog.find((c) => c.id === catalogItemId);
        if (!ref) return;
        setLignes((prev) => [...prev, {
            catalog_item_id: ref.id,
            label: ref.label,
            quantity: 1,
            unit_price_ttc: Number(ref.unit_price_ttc || 0),
            tax_rate: Number(ref.tax_rate || 0),
            unit_duration_minutes: Math.max(0, Number(ref.default_duration_minutes) || 0),
            category_id: ref.category_id ?? null,
            unit_type: ref.unit_type || 'unite',
        }]);
        setAjoutId('');
    };

    // Les prestations viennent du devis ; prix/TVA/durée sont réalignés sur le catalogue courant (le devis ne
    // porte pas la durée unitaire, nécessaire au calcul automatique de la durée du rendez-vous). La ligne du
    // devis peut pointer un identifiant de catalogue qui a changé depuis (catalogues Pro/Particulier
    // distincts, prestation recréée) : on retombe alors sur le libellé.
    const construireLignes = React.useCallback((itemsDevis: DevisItem[]): LigneRdv[] => {
        const items = itemsDevis.filter((l) => !estFraisKm(l));
        const cle = (s: string) => s.trim().toLowerCase();
        const parId = Object.fromEntries(catalog.map((c) => [c.id, c]));
        const parLibelle = Object.fromEntries(catalog.map((c) => [cle(c.label), c]));
        return items.map((l) => {
            const ref = (l.catalog_item_id ? parId[l.catalog_item_id] : null) || parLibelle[cle(l.label)] || null;
            return {
                catalog_item_id: ref?.id ?? l.catalog_item_id,
                label: l.label,
                quantity: l.quantity,
                // Le prix du devis signé fait foi : une option est à 0 € au catalogue et chiffrée au devis selon la prestation visée.
                unit_price_ttc: (l as any).list_unit_price_ttc ?? l.unit_price_ttc ?? ref?.unit_price_ttc ?? 0,
                tax_rate: l.tax_rate ?? ref?.tax_rate ?? 0,
                unit_duration_minutes: Math.max(0, Number(ref?.default_duration_minutes) || 0),
                category_id: ref?.category_id ?? null,
                unit_type: ref?.unit_type || 'unite',
            };
        });
    }, [catalog]);

    const planifier = async () => {
        if (planifiePassage == null || !crmContactId) return;
        const passage = passages.find((p) => p.numero === planifiePassage);
        if (!passage) return;
        if (!date) { setErreur('Choisissez une date.'); return; }
        if (employeIds.length === 0) { setErreur('Assignez au moins un salarié.'); return; }
        const catalogItems = lignes.filter((l) => l.quantity > 0).map(({ category_id, ...l }) => l);
        if (catalogItems.length === 0) { setErreur('Gardez au moins une prestation sur ce rendez-vous.'); return; }
        const manquantes = catalogItems.filter((l) => l.unit_duration_minutes <= 0 && !estFraisKm(l)).map((l) => l.label);
        if (manquantes.length > 0) { setErreur(`Indiquez la durée de : ${manquantes.join(', ')}.`); return; }
        setSaving(true); setErreur('');
        try {
            const res: any = await moduleApi.createPrestationAppointment({
                crm_contact_id: crmContactId,
                appointment_date: date,
                start_time: heure,
                employee_ids: employeIds,
                catalog_items: catalogItems,
                location_type: lieu,
                extra_emails: emailsSupp.split(/[,;\s]+/).filter(Boolean),
                formula_subscription_id: sansFormule ? null : formulaSubscriptionId,
                // Numéro imposé : un rattrapage reste sur le même passage, il ne consomme pas le suivant.
                formula_occurrence_number: sansFormule ? null : passage.numero,
                source_dossier_id: sansFormule ? dossierId : undefined,
                notes: sansFormule
                    ? "Prestation convenue au devis signé"
                    : `Passage ${passage.numero}${passage.periode ? ` — ${passage.periode}` : ''}${formulaInfo ? ` (formule ${formulaInfo.formula_name})` : ''}`,
            });
            if (res?.error) { setErreur(res.error); return; }
            setPlanifiePassage(null);
            onBooked(res.id, res.confirmation_warning || res.payment_warning || undefined);
        } catch (e: any) {
            setErreur(e?.message || 'Erreur lors de la planification.');
        } finally {
            setSaving(false);
        }
    };

    if (passages.length === 0) return null;
    const champ = 'w-full bg-surface border border-border rounded-lg px-3 py-2 text-sm text-content outline-none focus:border-brand-500';
    const passageEnCours = planifiePassage != null ? passages.find((p) => p.numero === planifiePassage) : null;
    // Aperçu de la durée avant de valider : somme(quantité × durée unitaire configurée) ÷ nombre de salariés
    // assignés — exactement le calcul que refait le serveur à la création.
    const dureeTravail = lignes.reduce((s, l) => s + l.quantity * l.unit_duration_minutes, 0);
    const dureeRdv = employeIds.length > 0 ? dureeTravail / employeIds.length : 0;
    const sansDuree = lignes.filter((l) => l.quantity > 0 && l.unit_duration_minutes <= 0 && !estFraisKm(l)).map((l) => l.label);
    const reglesFormule = Object.fromEntries((formulaInfo?.formula_items || []).map((r) => [r.catalog_item_id, r]));
    // Le seuil minimum ne se cumule pas sur l'année : CHAQUE passage doit l'atteindre individuellement
    // (même règle que FormulaPassagesPlanner et que le serveur).
    const seuils = formulaInfo?.category_thresholds || [];
    const totauxParCategorie = lignes.reduce<Record<string, number>>((acc, l) => {
        if (l.category_id && l.quantity > 0) acc[l.category_id] = (acc[l.category_id] || 0) + l.quantity;
        return acc;
    }, {});
    const seuilsAtteints = seuils.map((t) => ({ ...t, atteint: totauxParCategorie[t.category_id] || 0 }));
    // Seuil en montant (2026-09-08) : brut TTC du passage avant remise, comparé au minimum de la formule.
    // Passages libres : le minimum est annuel, il n'y a rien à contrôler passage par passage.
    const libres = !!Number(formulaInfo?.passages_libres);
    const seuilMontant = formulaInfo?.threshold_kind === 'montant'
        ? Math.max(0, Number(formulaInfo?.minimum_amount_ttc) || 0)
        : 0;
    const brutPassage = Math.round(lignes.reduce(
        (s, l) => s + (l.quantity > 0 ? l.quantity * (Number(l.unit_price_ttc) || 0) : 0), 0) * 100) / 100;
    const montantFr = (n: number) => n.toFixed(2).replace('.', ',');
    const seuilOk = seuilMontant > 0
        ? brutPassage >= seuilMontant
        : (seuils.length === 0 || (formulaInfo?.category_thresholds_mode === 'all'
            ? seuilsAtteints.every((t) => t.atteint >= t.minimum_quantity)
            : seuilsAtteints.some((t) => t.atteint >= t.minimum_quantity)));
    const dejaChoisis = new Set(lignes.map((l) => l.catalog_item_id).filter(Boolean) as string[]);

    return (
        <div className="space-y-2.5">
            <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-bold text-content flex items-center gap-2"><CalendarClock size={16} /> {sansFormule ? 'Rendez-vous de la prestation' : 'Passages de la formule'}</p>
                {formulaInfo && (
                    <span className="text-[11px] font-bold text-content-muted">
                        {formulaInfo.formula_name} — {libres ? `${formulaInfo.occurrences_used} réalisé${formulaInfo.occurrences_used > 1 ? 's' : ''} (passages libres)` : `${formulaInfo.occurrences_used}/${formulaInfo.occurrences_total} réalisés`}
                    </span>
                )}
            </div>

            <div className="space-y-2">
                {passages.map((p) => {
                    const rdvs = rdvsDuPassage(p.numero);
                    const suivi = avancements[p.numero];
                    const progression = suivi && !suivi.ancien ? suivi.avancement : null;
                    const realise = progression ? progression.complet : rdvs.some((r) => r.status === 'termine');
                    // Avec le suivi par visites, « planifié » = au moins une visite (même sans rendez-vous rattaché à la formule).
                    const planifie = rdvs.length > 0 || (!!progression && progression.nb_visites > 0);
                    const resteTotal = progression ? progression.lignes.reduce((n, l) => n + l.reste, 0) : 0;
                    return (
                        <div key={p.numero} className="p-3 rounded-xl bg-surface-soft border border-border space-y-1.5">
                            <div className="flex items-center justify-between gap-2 flex-wrap">
                                <span className="text-xs font-black uppercase tracking-widest text-content-muted flex items-center gap-1.5">
                                    {realise ? <CheckCircle2 size={13} className="text-green-500" /> : planifie ? <Clock size={13} className="text-amber-500" /> : <Circle size={13} className="opacity-40" />}
                                    {sansFormule ? 'Prestations convenues au devis' : `Passage ${p.numero}${p.periode ? ` — ${p.periode}` : ''}`}
                                </span>
                                {!planifie && <span className="text-[11px] font-bold text-content-muted">Non planifié</span>}
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                                {p.items.map((l, i) => (
                                    <span key={i} className="text-[11px] font-semibold px-2 py-1 rounded-lg bg-surface border border-border text-content-muted">
                                        {l.label} × {l.quantity}
                                    </span>
                                ))}
                            </div>
                            {progression && (
                                <div className="space-y-1">
                                    <div className="h-1.5 rounded-full bg-surface overflow-hidden" role="progressbar" aria-valuenow={progression.pourcentage} aria-valuemin={0} aria-valuemax={100} aria-label="Avancement du passage">
                                        <div className="h-full bg-brand-600" style={{ width: `${progression.pourcentage}%` }} />
                                    </div>
                                    <p className="text-[11px] text-content-muted">
                                        {progression.total_fait} sur {progression.total_prevu} réalisés
                                        {progression.nb_visites > 0 ? ` · ${progression.nb_visites} visite${progression.nb_visites > 1 ? 's' : ''}` : ''}
                                        {resteTotal > 0 ? ` · reste ${resteTotal} à planifier` : ''}
                                    </p>
                                </div>
                            )}
                            {rdvs.length > 0 && (
                                <div className="space-y-1 pt-1 border-t border-border">
                                    {rdvs.map((r) => (
                                        <p key={r.id} className={`text-[11px] font-bold flex items-center gap-1.5 ${r.status === 'termine' ? 'text-green-500' : 'text-amber-500'}`}>
                                            {r.status === 'termine' ? <CheckCircle2 size={12} /> : <Clock size={12} />}
                                            {r.status === 'termine'
                                                ? `Réalisé le ${fmtDate(r.completed_at || r.appointment_date)}`
                                                : `Planifié le ${fmtDate(r.appointment_date)} à ${r.start_time}`}
                                        </p>
                                    ))}
                                </div>
                            )}
                            <Button variant="ghost" onClick={() => ouvrirPlanification(p.numero)}>
                                <CalendarClock size={13} /> {progression && resteTotal > 0 && planifie ? `Planifier la suite (reste ${resteTotal})` : planifie ? 'Planifier un rendez-vous complémentaire' : sansFormule ? 'Planifier le rendez-vous' : 'Planifier ce passage'}
                            </Button>
                        </div>
                    );
                })}
            </div>

            {passageEnCours && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[80] flex items-center justify-center p-4">
                    <div className="w-full max-w-md bg-surface border border-border rounded-3xl shadow-2xl max-h-[85vh] flex flex-col overflow-hidden">
                        <div className="p-5 border-b border-border">
                            <h3 className="font-bold text-content">{sansFormule ? 'Planifier le rendez-vous' : `Planifier le passage ${passageEnCours.numero}`}</h3>
                            <p className="text-xs text-content-muted mt-0.5">Les prestations sont reprises du devis signé — vous ne saisissez que la date, l'heure et l'équipe.</p>
                        </div>
                        <div className="p-5 space-y-3 overflow-y-auto">
                            <div className="p-3 rounded-xl bg-surface-soft border border-border space-y-2">
                                <p className="text-[10px] font-black uppercase tracking-widest text-content-muted">
                                    {sansFormule ? 'Prestations du rendez-vous' : 'Prestations de ce passage'} <span className="font-bold normal-case tracking-normal opacity-70">— reprises du devis, modifiables</span>
                                </p>
                                {lignes.map((l, i) => {
                                    const benef = beneficeFormule(l.catalog_item_id ? reglesFormule[l.catalog_item_id] : undefined, passageEnCours.numero);
                                    return (
                                        <div key={i} className="space-y-1.5">
                                        <div className="flex items-center gap-2">
                                            <div className="min-w-0 flex-1">
                                                <p className="text-xs text-content truncate" title={l.label}>{l.label}</p>
                                                <p className="text-[10px] text-content-muted flex items-center gap-1.5">
                                                    {estFraisKm(l) ? null : l.catalog_item_id && l.unit_duration_minutes > 0 ? (
                                                        <span>{l.unit_duration_minutes} min/{uniteCourte(l.unit_type)}</span>
                                                    ) : (
                                                        <label className="inline-flex items-center gap-1">
                                                            <span className={l.unit_duration_minutes > 0 ? '' : 'text-amber-500 font-bold'}>Durée :</span>
                                                            <input type="number" min={0} step={5} value={l.unit_duration_minutes || ''} placeholder="min"
                                                                onChange={(e) => changerDuree(i, Number(e.target.value))}
                                                                className={`w-14 bg-surface border rounded px-1.5 py-0.5 text-[11px] text-content text-right outline-none focus:border-brand-500 ${l.unit_duration_minutes > 0 ? 'border-border' : 'border-amber-500/60'}`} />
                                                            <span>min/{uniteCourte(l.unit_type)}</span>
                                                        </label>
                                                    )}
                                                    {benef && (
                                                        <span className={`font-black px-1.5 py-px rounded ${benef.offert ? 'bg-green-500/15 text-green-500' : 'bg-brand-500/15 text-brand-600 dark:text-brand-400'}`}>
                                                            {benef.texte}
                                                        </span>
                                                    )}
                                                </p>
                                            </div>
                                            <input type="number" min={0} step={1} value={l.quantity}
                                                onChange={(e) => changerQuantite(i, Number(e.target.value))}
                                                className="w-16 shrink-0 bg-surface border border-border rounded-lg px-2 py-1 text-sm text-content text-right outline-none focus:border-brand-500" />
                                            <span className="shrink-0 w-6 text-[11px] font-bold text-content-muted">{uniteCourte(l.unit_type)}</span>
                                            <button type="button" onClick={() => retirerLigne(i)} title="Retirer cette prestation"
                                                className="shrink-0 text-content-muted hover:text-red-500 transition-colors"><Trash2 size={14} /></button>
                                        </div>
                                        {!estFraisKm(l) && <RemiseExtraField extra={l} onChange={(patch) => setLignes((prev) => prev.map((x, k) => (k === i ? { ...x, ...patch } : x)))} />}
                                        </div>
                                    );
                                })}
                                <select value={ajoutId} onChange={(e) => ajouterLigne(e.target.value)} className={champ}>
                                    <option value="">+ Ajouter une prestation…</option>
                                    {catalog.filter((c) => !dejaChoisis.has(c.id)).map((c) => (
                                        <option key={c.id} value={c.id}>{c.label}</option>
                                    ))}
                                </select>
                                {!sansFormule && <p className="text-[10px] text-content-muted italic">
                                    Les réductions et gratuités de la formule s'appliquent automatiquement à la facturation, y compris sur une prestation ajoutée ici, dès qu'elle fait partie de la formule.
                                </p>}
                            </div>

                            {!libres && seuilMontant > 0 && (
                                <div className={`p-3 rounded-xl border space-y-1 ${seuilOk ? 'bg-surface-soft border-border' : 'bg-amber-500/10 border-amber-500/25'}`}>
                                    <p className="text-[10px] font-black uppercase tracking-widest text-content-muted">
                                        Minimum requis pour ce passage
                                    </p>
                                    <p className={`text-xs font-bold flex items-center gap-1.5 ${seuilOk ? 'text-green-500' : 'text-amber-500'}`}>
                                        {seuilOk ? <CheckCircle2 size={13} /> : <AlertCircle size={13} />} {montantFr(brutPassage)} € / {montantFr(seuilMontant)} € TTC
                                    </p>
                                    {!seuilOk && <p className="text-[11px] text-content-muted">Le minimum de la formule n'est pas atteint sur ce passage — vous pouvez quand même planifier, mais le client sort des conditions de sa formule.</p>}
                                </div>
                            )}
                            {!libres && seuilMontant <= 0 && seuils.length > 0 && (
                                <div className={`p-3 rounded-xl border space-y-1 ${seuilOk ? 'bg-surface-soft border-border' : 'bg-amber-500/10 border-amber-500/25'}`}>
                                    <p className="text-[10px] font-black uppercase tracking-widest text-content-muted">
                                        Minimum requis pour ce passage ({formulaInfo?.category_thresholds_mode === 'all' ? 'tous requis' : 'un seul suffit'})
                                    </p>
                                    {seuilsAtteints.map((t) => {
                                        const ok = t.atteint >= t.minimum_quantity;
                                        return (
                                            <p key={t.category_id} className={`text-xs font-bold flex items-center gap-1.5 ${ok ? 'text-green-500' : 'text-amber-500'}`}>
                                                {ok ? <CheckCircle2 size={13} /> : <AlertCircle size={13} />} {t.category_name} : {t.atteint}/{t.minimum_quantity}
                                            </p>
                                        );
                                    })}
                                    {!seuilOk && <p className="text-[11px] text-content-muted">Le minimum de la formule n'est pas atteint sur ce passage — vous pouvez quand même planifier, mais le client sort des conditions de sa formule.</p>}
                                </div>
                            )}
                            <div className="grid grid-cols-2 gap-2">
                                <div className="space-y-1">
                                    <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Date</label>
                                    <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={champ} />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Heure</label>
                                    <input type="time" value={heure} onChange={(e) => setHeure(e.target.value)} className={champ} />
                                </div>
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Lieu</label>
                                <select value={lieu} onChange={(e) => setLieu(e.target.value as 'domicile' | 'entreprise')} className={champ}>
                                    <option value="domicile">Chez le client</option>
                                    <option value="entreprise">Dans nos locaux</option>
                                </select>
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Envoyer aussi la confirmation à (facultatif)</label>
                                <input value={emailsSupp} onChange={(e) => setEmailsSupp(e.target.value)} placeholder="autre@exemple.fr, encore.un@exemple.fr" className={champ} />
                                <p className="text-[11px] text-content-muted">La confirmation part à l'adresse de la fiche client et à celles-ci, en un seul envoi.</p>
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Salarié(s) assigné(s)</label>
                                <div className="flex flex-wrap gap-1.5">
                                    {employes.map((e) => {
                                        const actif = employeIds.includes(String(e.id));
                                        return (
                                            <button key={e.id} type="button"
                                                onClick={() => setEmployeIds((prev) => actif ? prev.filter((x) => x !== String(e.id)) : [...prev, String(e.id)])}
                                                className={`px-2.5 py-1.5 rounded-lg text-xs font-bold border transition-all ${actif ? 'bg-brand-600 border-brand-600 text-brand-fg' : 'border-border text-content-muted hover:text-content'}`}>
                                                {e.first_name} {e.last_name}
                                            </button>
                                        );
                                    })}
                                    {employes.length === 0 && <p className="text-xs text-content-muted italic">Aucun salarié configuré (Postes &amp; Employés).</p>}
                                </div>
                            </div>
                            {dureeTravail > 0 && employeIds.length > 0 && (
                                <div className="flex items-center gap-2 p-3 rounded-xl bg-brand-500/10 border border-brand-500/20 text-xs text-content-muted">
                                    <Clock size={14} className="text-brand-600 dark:text-brand-400 shrink-0" />
                                    Durée du rendez-vous : <strong className="text-content">{formatDureeMinutes(dureeRdv)}</strong> ({formatDureeMinutes(dureeTravail)} de travail ÷ {employeIds.length} salarié{employeIds.length > 1 ? 's' : ''})
                                </div>
                            )}
                            {sansDuree.length > 0 && (
                                <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-500/10 border border-amber-500/25 text-xs text-content-muted">
                                    <Clock size={14} className="text-amber-500 shrink-0 mt-0.5" />
                                    <span>
                                        <strong className="text-content">Durée manquante</strong> pour {sansDuree.join(', ')}.
                                        Saisissez-la sur la ligne ci-dessus (ex. 30 min) : sans elle le rendez-vous est créé avec une durée nulle. Pour une prestation du catalogue, vous pouvez aussi fixer sa durée une fois pour toutes dans <strong className="text-content">Prestations</strong>.
                                    </span>
                                </div>
                            )}
                            {erreur && <p className="text-xs text-red-500 font-semibold">{erreur}</p>}
                        </div>
                        <div className="flex justify-end gap-3 p-5 border-t border-border">
                            <button onClick={() => setPlanifiePassage(null)} className="px-4 py-2.5 rounded-xl border border-border text-content-muted hover:text-content text-sm font-bold">Annuler</button>
                            <button onClick={planifier} disabled={saving}
                                className="px-5 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg text-sm font-bold disabled:opacity-50 flex items-center gap-1.5">
                                {saving ? <Loader2 size={14} className="animate-spin" /> : <CalendarClock size={14} />} Planifier
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
